import type {
  CashFlowEntry,
  CashFlowLedger,
  FacilityEffects,
  FacilityLevel,
  FacilityType,
  GameState,
  MatchDayRevenue,
  PerformanceClause,
  Sponsor,
  SponsorTier,
  SponsorshipState,
  WeeklyOpsCosts,
  BroadcastRevenue,
} from './types'
import { buildLadder } from './season'

const SPONSOR_TIER_DEFAULTS: Record<SponsorTier, { count: number; baseValue: number; clauseCount: number }> = {
  major: { count: 1, baseValue: 1200, clauseCount: 3 },
  secondary: { count: 2, baseValue: 500, clauseCount: 2 },
  community: { count: 3, baseValue: 150, clauseCount: 1 },
}

const MAJOR_SPONSORS = [
  { name: 'Imperial Mining', id: 'sponsor-imperial-mining' },
  { name: 'Kingsport Port Authority', id: 'sponsor-kpa' },
  { name: 'Southern Cross Energy', id: 'sponsor-sce' },
  { name: 'Apex Logistics', id: 'sponsor-apex' },
]

const SECONDARY_SPONSORS = [
  { name: 'Harbour City Motors', id: 'sponsor-hcm' },
  { name: 'Southern Cross Finance', id: 'sponsor-vale-finance' },
  { name: 'Riverland Agri', id: 'sponsor-riverland-agri' },
  { name: 'Geelong Glass', id: 'sponsor-geelong-glass' },
  { name: 'Harbour Foundry Group', id: 'sponsor-foley-group' },
  { name: 'Coastal Insurance', id: 'sponsor-coastal-insurance' },
]

const COMMUNITY_SPONSORS = [
  { name: "Kingsport Baker's Dozen", id: 'sponsor-bakers-dozen' },
  { name: 'Imperial Supporters Club', id: 'sponsor-supporters-club' },
  { name: 'Docklands Community Bank', id: 'sponsor-docklands-bank' },
  { name: 'Northside Physio', id: 'sponsor-northside-physio' },
  { name: 'Maritime Meats', id: 'sponsor-maritime-meats' },
  { name: 'Harbour View Café', id: 'sponsor-harbour-view-cafe' },
  { name: 'West End Tavern', id: 'sponsor-west-end-tavern' },
  { name: 'Southgate Realty', id: 'sponsor-southgate-realty' },
]

const hash = (value: string) => {
  let result = 2166136261
  for (let index = 0; index < value.length; index += 1) result = Math.imul(result ^ value.charCodeAt(index), 16777619)
  return result >>> 0
}

const seeded = (seed: number, key: string, min: number, max: number) => min + (hash(`${seed}:${key}`) % (max - min + 1))
const money = (value: number) => Math.round(value * 100) / 100
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))
const thousandsToMillions = (value: number) => value / 1000
const millionsToThousands = (value: number) => value * 1000

function generatePerformanceClauses(tier: SponsorTier, seed: number, sponsorIndex: number): PerformanceClause[] {
  const clauseCount = SPONSOR_TIER_DEFAULTS[tier].clauseCount
  const clauses: PerformanceClause[] = []

  const clauseTypes: PerformanceClause['type'][] = ['ladder-position', 'finals-appearance', 'home-wins', 'attendance-milestone', 'player-milestone']

  for (let i = 0; i < clauseCount; i++) {
    const type = clauseTypes[seeded(seed, `${sponsorIndex}:clause:${i}:type`, 0, clauseTypes.length - 1)]
    let threshold: number
    let bonus: number
    let description: string

    switch (type) {
      case 'ladder-position':
        threshold = seeded(seed, `${sponsorIndex}:clause:${i}:threshold`, 1, 8)
        bonus = tier === 'major' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 200, 400) :
              tier === 'secondary' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 75, 150) :
              seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 20, 50)
        description = `Finish top ${threshold} on the ladder`
        break
      case 'finals-appearance':
        threshold = 1
        bonus = tier === 'major' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 150, 300) :
              tier === 'secondary' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 50, 100) :
              seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 15, 35)
        description = 'Qualify for finals'
        break
      case 'home-wins':
        threshold = seeded(seed, `${sponsorIndex}:clause:${i}:threshold`, 4, 8)
        bonus = tier === 'major' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 100, 200) :
              tier === 'secondary' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 40, 80) :
              seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 10, 30)
        description = `Win ${threshold}+ home games`
        break
      case 'attendance-milestone':
        threshold = seeded(seed, `${sponsorIndex}:clause:${i}:threshold`, 15000, 30000)
        bonus = tier === 'major' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 100, 200) :
              tier === 'secondary' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 40, 80) :
              seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 10, 30)
        description = `Average home attendance ≥ ${threshold.toLocaleString()}`
        break
      case 'player-milestone':
        threshold = seeded(seed, `${sponsorIndex}:clause:${i}:threshold`, 50, 200)
        bonus = tier === 'major' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 75, 150) :
              tier === 'secondary' ? seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 30, 60) :
              seeded(seed, `${sponsorIndex}:clause:${i}:bonus`, 10, 25)
        description = `A player reaches ${threshold} senior games`
        break
    }

    clauses.push({ type, threshold, bonus, description, earned: false })
  }

  return clauses
}

function createSponsor(tier: SponsorTier, index: number, seed: number, season: number): Sponsor {
  const defaults = SPONSOR_TIER_DEFAULTS[tier]
  const pools = { major: MAJOR_SPONSORS, secondary: SECONDARY_SPONSORS, community: COMMUNITY_SPONSORS }
  const pool = pools[tier]
  const poolIndex = (index + seeded(seed, `${tier}:pool:${index}`, 0, pool.length - 1)) % pool.length
  const base = pool[poolIndex]

  const baseValue = money(defaults.baseValue * (0.9 + seeded(seed, `${tier}:value:${index}`, 0, 20) / 100))
  const contractYears = seeded(seed, `${tier}:years:${index}`, 1, 3)

  return {
    id: base.id,
    name: base.name,
    tier,
    baseValue,
    performanceClauses: generatePerformanceClauses(tier, seed, index),
    currentSeasonValue: baseValue,
    contractSeason: season,
    contractYears,
  }
}

export function createInitialSponsorshipState(seed: number, season = 1): SponsorshipState {
  const sponsors: Sponsor[] = []
  let sponsorIndex = 0

  for (const tier of ['major', 'secondary', 'community'] as SponsorTier[]) {
    const count = SPONSOR_TIER_DEFAULTS[tier].count
    for (let i = 0; i < count; i++) {
      sponsors.push(createSponsor(tier, sponsorIndex, seed, season))
      sponsorIndex++
    }
  }

  return {
    version: 52,
    sponsors,
    processedRounds: [],
    processedSeasons: [],
  }
}

export function calculateStadiumCapacity(facilities: FacilityEffects): number {
  return facilities.stadiumCapacity
}

function calculateOpponentDrawFactor(opponentName: string, seasonSeed: number, round: number): number {
  const base = 0.7 + (hash(`${seasonSeed}:${opponentName}:draw`) % 60) / 100 // 0.7 - 1.3
  const roundVariation = (hash(`${seasonSeed}:round:${round}:variation`) % 21 - 10) / 100 // -0.1 to +0.1
  return clamp(base + roundVariation, 0.6, 1.4)
}

function calculateAttendance(capacity: number, ladderPosition: number, opponentDrawFactor: number, round: number, seasonSeed: number): number {
  const baseFill = 0.35 + (hash(`${seasonSeed}:attendance:base`) % 30) / 100 // 0.35 - 0.65
  const ladderBonus = Math.max(0, (18 - ladderPosition) / 18) * 0.25 // up to +25% for top position
  const roundFactor = round <= 3 ? 0.9 : round >= 20 ? 1.1 : 1.0 // early/late season adjustments
  const fillRate = clamp(baseFill + ladderBonus, 0.25, 0.95) * opponentDrawFactor * roundFactor
  return Math.round(capacity * fillRate)
}

const TICKET_PRICE = 85 // $ per ticket
const MATCH_DAY_COST_RATE = 0.15 // 15% of gross for match-day ops

export function calculateMatchDayRevenue(
  state: GameState,
  fixtureId: string,
  venue: 'home' | 'away',
  opponentName: string,
  round: number
): MatchDayRevenue {
  const capacity = calculateStadiumCapacity(state.facilities.effects)
  const ladder = state.season ? buildLadderForRevenue(state.season, round - 1) : []
  const ladderPosition = ladder.findIndex(row => row.teamId === state.season?.userTeamId) + 1 || 10
  const opponentDrawFactor = venue === 'home' ? calculateOpponentDrawFactor(opponentName, state.season?.seed ?? state.seed, round) : 0
  const attendance = venue === 'home' ? calculateAttendance(capacity, ladderPosition, opponentDrawFactor, round, state.season?.seed ?? state.seed) : 0
  const grossRevenue = money(attendance * TICKET_PRICE / 1000) // $k
  const netRevenue = money(grossRevenue * (1 - MATCH_DAY_COST_RATE))

  return {
    round,
    fixtureId,
    venue,
    attendance,
    capacity,
    ticketPrice: TICKET_PRICE,
    opponentDrawFactor: money(opponentDrawFactor),
    grossRevenue,
    netRevenue,
  }
}

function buildLadderForRevenue(season: import('./types').SeasonState, throughRound: number) {
  return buildLadder(season, throughRound)
}

export function calculateBroadcastRevenue(state: GameState, season: number): BroadcastRevenue {
  const ladder = state.season ? buildLadderForRevenue(state.season, state.season.completedThroughRound) : []
  const ladderPosition = ladder.findIndex(row => row.teamId === state.season?.userTeamId) + 1 || 10
  const teamCount = ladder.length || 6

  // Pool share: higher ladder position = larger share
  // Top team gets ~2.5x the bottom team's share
  const basePool = 4500 // $4.5M total pool per team (in $k)
  const positionMultiplier = 1.5 + (teamCount - ladderPosition) / (teamCount - 1) // 1.5 to 2.5
  const poolShare = money(basePool * positionMultiplier)

  // Performance bonus for top 4, top 8
  let performanceBonus = 0
  if (ladderPosition <= 4) performanceBonus += 800
  if (ladderPosition <= 8) performanceBonus += 300

  return {
    season,
    ladderPosition,
    poolShare,
    performanceBonus,
    total: money(poolShare + performanceBonus),
  }
}

const STAFF_WAGE_PER_COACH = 500 // $k per assistant coach per season
const TRAVEL_COST_HOME = 0
const TRAVEL_COST_AWAY = 80 // $k per away game
const FACILITY_UPKEEP_PER_LEVEL = 50 // $k per facility level per season
const OTHER_OPS_BASE = 800 // $k base admin/medical per season

export function calculateWeeklyOpsCosts(state: GameState, round: number): WeeklyOpsCosts {

  const playerWages = money(state.club.salaryCap.committedCap / 22) // weekly = annual / 22 rounds
  const staffWages = money(state.staffMarket.roster.length * STAFF_WAGE_PER_COACH / 22)
  const travel = state.week?.venue === 'away' ? TRAVEL_COST_AWAY : TRAVEL_COST_HOME
  const facilityLevels = Object.values(state.facilities.levels)
  const facilityUpkeep = money(facilityLevels.reduce((sum: number, level: FacilityLevel) => sum + level, 0) * FACILITY_UPKEEP_PER_LEVEL / 22)
  const other = money(OTHER_OPS_BASE / 22)
  const total = playerWages + staffWages + travel + facilityUpkeep + other

  return {
    round,
    playerWages,
    staffWages,
    travel,
    facilityUpkeep,
    other,
    total,
  }
}

function addCashFlowEntry(
  ledger: CashFlowLedger,
  season: number,
  round: number,
  category: CashFlowEntry['category'],
  description: string,
  amount: number
): CashFlowLedger {
  const newBalance = money(ledger.currentBalance + amount)
  const entry: CashFlowEntry = {
    id: `cf-${season}-${round}-${category}-${ledger.entries.length + 1}`,
    season,
    round,
    category,
    description,
    amount,
    balanceAfter: newBalance,
  }
  return {
    ...ledger,
    entries: [...ledger.entries, entry],
    currentBalance: newBalance,
  }
}

export function processWeeklyRevenue(state: GameState): GameState {
  const season = state.season?.season ?? 1
  const round = state.round
  const roundKey = `s${season}-r${round}`

  if (state.sponsorship.processedRounds.includes(roundKey)) return state
  const isBye = state.week?.venue === 'bye'
  if ((!state.matchResult || !state.consequenceReport) && !isBye) return state

  const opponentName = state.opponent.name
  const fixtureId = state.week?.fixtureId ?? ''

  // Match-day revenue (a bye has no gate, but all other weekly ledgers still run).
  const matchDay = isBye ? null : calculateMatchDayRevenue(state, fixtureId, state.week?.venue === 'away' ? 'away' : 'home', opponentName, round)

  // Weekly ops costs
  const opsCosts = calculateWeeklyOpsCosts(state, round)

  // Sponsorship installment (1/22 of current season value per round)
  const sponsorshipInstallment = money(
    state.sponsorship.sponsors.reduce((sum, s) => sum + s.currentSeasonValue, 0) / 22
  )

  let ledger = state.cashFlowLedger
  let cash = state.club.resources.cash

  // Match-day net revenue
  if (matchDay && matchDay.netRevenue > 0) {
    ledger = addCashFlowEntry(ledger, season, round, 'match-day', `Match-day revenue vs ${opponentName} (${matchDay.venue})`, matchDay.netRevenue)
    cash = money(cash + thousandsToMillions(matchDay.netRevenue))
  }

  // Sponsorship installment
  if (sponsorshipInstallment > 0) {
    ledger = addCashFlowEntry(ledger, season, round, 'sponsorship', 'Sponsorship installment', sponsorshipInstallment)
    cash = money(cash + thousandsToMillions(sponsorshipInstallment))
  }

  // Weekly ops costs
  if (opsCosts.total > 0) {
    ledger = addCashFlowEntry(ledger, season, round, 'ops-costs', `Weekly ops: wages ${opsCosts.playerWages}k, staff ${opsCosts.staffWages}k, travel ${opsCosts.travel}k, facility ${opsCosts.facilityUpkeep}k, other ${opsCosts.other}k`, -opsCosts.total)
    cash = money(cash - thousandsToMillions(opsCosts.total))
  }

  const processedRounds = [...state.sponsorship.processedRounds, roundKey]

  return {
    ...state,
    club: {
      ...state.club,
      resources: { ...state.club.resources, cash },
    },
    sponsorship: { ...state.sponsorship, processedRounds },
    cashFlowLedger: { ...ledger, processedRounds: [...new Set([...(ledger.processedRounds ?? []), roundKey])] },
  }
}

export function checkSponsorPerformanceClauses(state: GameState): GameState {
  const season = state.season?.season ?? 1
  if (state.sponsorship.processedSeasons.includes(season)) return state

  const ladder = state.season ? buildLadderForRevenue(state.season, state.season.completedThroughRound) : []
  const ladderPosition = ladder.findIndex(row => row.teamId === state.season?.userTeamId) + 1 || 10
  const homeWins = state.club.record.wins // simplified - would need home/away split

  let sponsorsChanged = false
  const updatedSponsors = state.sponsorship.sponsors.map(sponsor => {
    let currentValue = sponsor.baseValue
    let clausesChanged = false
    const updatedClauses = sponsor.performanceClauses.map(clause => {
      if (clause.earned) return clause
      let earned = false
      switch (clause.type) {
        case 'ladder-position':
          earned = ladderPosition <= clause.threshold
          break
        case 'finals-appearance':
          earned = ladderPosition <= 8 // top 8 = finals
          break
        case 'home-wins':
          earned = homeWins >= clause.threshold
          break
        case 'attendance-milestone':
          // Would need attendance tracking - simplified for now
          earned = false
          break
        case 'player-milestone':
          // Would need player milestone tracking - simplified for now
          earned = false
          break
      }
      if (earned) {
        currentValue += clause.bonus
        clausesChanged = true
        sponsorsChanged = true
      }
      return { ...clause, earned }
    })
    return clausesChanged ? { ...sponsor, performanceClauses: updatedClauses, currentSeasonValue: currentValue } : sponsor
  })

  if (!sponsorsChanged) {
    return { ...state, sponsorship: { ...state.sponsorship, processedSeasons: [...state.sponsorship.processedSeasons, season] } }
  }

  // Add performance bonuses to cash flow
  let ledger = state.cashFlowLedger
  let cash = state.club.resources.cash
  const totalBonus = updatedSponsors.reduce((sum, s) => sum + s.currentSeasonValue, 0) - state.sponsorship.sponsors.reduce((sum, s) => sum + s.currentSeasonValue, 0)

  if (totalBonus > 0) {
    ledger = addCashFlowEntry(ledger, season, 0, 'sponsorship', 'Season performance bonuses earned', totalBonus)
    cash = money(cash + thousandsToMillions(totalBonus))
  }

  return {
    ...state,
    club: { ...state.club, resources: { ...state.club.resources, cash } },
    sponsorship: { ...state.sponsorship, sponsors: updatedSponsors, processedSeasons: [...state.sponsorship.processedSeasons, season] },
    cashFlowLedger: ledger,
  }
}

export function processSeasonEndRevenue(state: GameState): GameState {
  const season = state.season?.season ?? 1
  if (state.sponsorship.processedSeasons.includes(season)) return state

  // Broadcast revenue
  const broadcast = calculateBroadcastRevenue(state, season)

  let ledger = state.cashFlowLedger
  let cash = state.club.resources.cash

  ledger = addCashFlowEntry(ledger, season, 0, 'broadcast', `Broadcast pool share (ladder ${broadcast.ladderPosition})`, broadcast.poolShare)
  cash = money(cash + thousandsToMillions(broadcast.poolShare))

  if (broadcast.performanceBonus > 0) {
    ledger = addCashFlowEntry(ledger, season, 0, 'broadcast', `Broadcast performance bonus (top ${broadcast.ladderPosition <= 4 ? 4 : 8})`, broadcast.performanceBonus)
    cash = money(cash + thousandsToMillions(broadcast.performanceBonus))
  }

  // Sponsorship performance clauses check
  const withClauses = checkSponsorPerformanceClauses({ ...state, club: { ...state.club, resources: { ...state.club.resources, cash } }, cashFlowLedger: ledger })

  // Decrement sponsor contract years
  const agedSponsors = withClauses.sponsorship.sponsors.map(sponsor => ({
    ...sponsor,
    contractYears: Math.max(0, sponsor.contractYears - 1),
    currentSeasonValue: sponsor.baseValue, // reset for next season
    performanceClauses: sponsor.performanceClauses.map(c => ({ ...c, earned: false })),
  }))

  // Remove expired sponsors and generate new ones (simplified - just keep for now)
  const activeSponsors = agedSponsors.filter(s => s.contractYears > 0)

  return {
    ...withClauses,
    club: { ...withClauses.club, resources: { ...withClauses.club.resources, cash } },
    sponsorship: { ...withClauses.sponsorship, sponsors: activeSponsors, processedSeasons: [...withClauses.sponsorship.processedSeasons, season] },
  }
}

const EMPTY_LEDGER: CashFlowLedger = {
  version: 52,
  entries: [],
  currentBalance: 0,
  processedRounds: [],
}

type LegacyPersistedGameState = Omit<GameState, 'cashFlowLedger'> & { cashFlowLedger?: unknown }
type UnknownRecord = Record<string, unknown>

const CASH_FLOW_CATEGORIES = [
  'match-day', 'broadcast', 'sponsorship', 'ops-costs', 'facility-build', 'staff-market', 'contracts', 'other',
] as const satisfies readonly CashFlowEntry['category'][]
const SPONSOR_TIERS = ['major', 'secondary', 'community'] as const satisfies readonly SponsorTier[]
const PERFORMANCE_CLAUSE_TYPES = [
  'ladder-position', 'finals-appearance', 'home-wins', 'attendance-milestone', 'player-milestone',
] as const satisfies readonly PerformanceClause['type'][]

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null
const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)
const isCashFlowCategory = (value: unknown): value is CashFlowEntry['category'] =>
  typeof value === 'string' && (CASH_FLOW_CATEGORIES as readonly string[]).includes(value)
const isSponsorTier = (value: unknown): value is SponsorTier =>
  typeof value === 'string' && (SPONSOR_TIERS as readonly string[]).includes(value)
const isPerformanceClauseType = (value: unknown): value is PerformanceClause['type'] =>
  typeof value === 'string' && (PERFORMANCE_CLAUSE_TYPES as readonly string[]).includes(value)

function hydrateCashFlowEntry(value: unknown): CashFlowEntry | null {
  if (!isRecord(value) || typeof value.id !== 'string' || !isFiniteNumber(value.amount) || !isFiniteNumber(value.balanceAfter)) return null
  return {
    id: value.id,
    season: isFiniteNumber(value.season) ? Math.max(1, Math.floor(value.season)) : 1,
    round: isFiniteNumber(value.round) ? Math.max(0, Math.floor(value.round)) : 0,
    category: isCashFlowCategory(value.category) ? value.category : 'other',
    description: typeof value.description === 'string' ? value.description : 'Cash flow entry',
    amount: value.amount,
    balanceAfter: value.balanceAfter,
  }
}

const migrateLegacyCashUnits = (entries: CashFlowEntry[], fallbackCash: number) => {
  const initial = entries.find(entry => entry.id === 'cf-initial')
  const usesLegacyMixedUnits = Boolean(initial && Math.abs(initial.amount) < 1000)
  if (!usesLegacyMixedUnits) return { entries, cash: fallbackCash, migrated: false }

  let balance = 0
  const migratedEntries = entries.map(entry => {
    // Weekly operating entries were already recorded in $k. The starting
    // balance and direct club transactions were incorrectly recorded in $m.
    const amount = entry.id === 'cf-initial'
      || entry.category === 'facility-build'
      || entry.category === 'staff-market'
      || entry.category === 'contracts'
      ? millionsToThousands(entry.amount)
      : entry.amount
    balance = money(balance + amount)
    return { ...entry, amount, balanceAfter: balance }
  })
  return { entries: migratedEntries, cash: money(thousandsToMillions(balance)), migrated: true }
}

function hydratePerformanceClause(value: unknown): PerformanceClause | null {
  if (!isRecord(value)) return null
  return {
    type: isPerformanceClauseType(value.type) ? value.type : 'ladder-position',
    threshold: isFiniteNumber(value.threshold) ? Math.max(1, Math.floor(value.threshold)) : 1,
    bonus: isFiniteNumber(value.bonus) && value.bonus >= 0 ? value.bonus : 0,
    description: typeof value.description === 'string' ? value.description : 'Performance clause',
    earned: typeof value.earned === 'boolean' ? value.earned : false,
  }
}

function hydrateSponsor(value: unknown, index: number, season: number): Sponsor | null {
  if (!isRecord(value)) return null
  const tier = isSponsorTier(value.tier) ? value.tier : 'community'
  const defaults = SPONSOR_TIER_DEFAULTS[tier]
  const performanceClauses = Array.isArray(value.performanceClauses)
    ? value.performanceClauses.map(hydratePerformanceClause).filter((clause): clause is PerformanceClause => clause !== null)
    : []
  return {
    id: typeof value.id === 'string' && value.id ? value.id : `sponsor-${tier}-${index}`,
    name: typeof value.name === 'string' && value.name ? value.name : `Sponsor ${index + 1}`,
    tier,
    baseValue: isFiniteNumber(value.baseValue) && value.baseValue > 0 ? value.baseValue : defaults.baseValue,
    performanceClauses,
    currentSeasonValue: isFiniteNumber(value.currentSeasonValue) ? value.currentSeasonValue : defaults.baseValue,
    contractSeason: isFiniteNumber(value.contractSeason) ? Math.max(1, Math.floor(value.contractSeason)) : season,
    contractYears: isFiniteNumber(value.contractYears) ? Math.max(0, Math.floor(value.contractYears)) : 1,
  }
}

export function createInitialCashFlowLedger(initialCash: number): CashFlowLedger {
  const initialCashInThousands = millionsToThousands(initialCash)
  return {
    ...EMPTY_LEDGER,
    currentBalance: initialCashInThousands,
    entries: [{
      id: 'cf-initial',
      season: 1,
      round: 0,
      category: 'other',
      description: 'Starting balance',
      amount: initialCashInThousands,
      balanceAfter: initialCashInThousands,
    }],
  }
}

export function hydrateSponsorship(state: GameState, saved?: unknown): GameState {
  const season = state.season?.season ?? 1
  const seed = state.season?.seed ?? state.seed

  if (!isRecord(saved)) {
    const initial = createInitialSponsorshipState(seed, season)
    return { ...state, sponsorship: initial, cashFlowLedger: createInitialCashFlowLedger(state.club.resources.cash) }
  }

  // Narrow every persisted level before dereferencing it. Invalid container
  // values are discarded, while malformed objects are repaired field-by-field.
  const validSponsors = (Array.isArray(saved.sponsors) ? saved.sponsors : [])
    .map((sponsor, index) => hydrateSponsor(sponsor, index, season))
    .filter((sponsor): sponsor is Sponsor => sponsor !== null)

  // Ensure we have at least the minimum sponsors per tier
  const tiersPresent = validSponsors.reduce((acc, sponsor) => {
    acc[sponsor.tier] = (acc[sponsor.tier] || 0) + 1
    return acc
  }, {} as Record<SponsorTier, number>)
  let nextId = validSponsors.length
  for (const tier of SPONSOR_TIERS) {
    const needed = SPONSOR_TIER_DEFAULTS[tier].count - (tiersPresent[tier] || 0)
    for (let i = 0; i < needed; i++) {
      validSponsors.push(createSponsor(tier, nextId, seed, season))
      nextId++
    }
  }

  const processedRounds = Array.isArray(saved.processedRounds)
    ? [...new Set(saved.processedRounds.filter((value): value is string => typeof value === 'string'))]
    : []
  const processedSeasons = Array.isArray(saved.processedSeasons)
    ? [...new Set(saved.processedSeasons.filter(isFiniteNumber))]
    : []

  const sponsorship: SponsorshipState = {
    version: 52,
    sponsors: validSponsors,
    processedRounds,
    processedSeasons,
  }

  // Hydrate cash flow ledger
  const savedLedger: unknown = (state as unknown as LegacyPersistedGameState).cashFlowLedger
  let ledger: CashFlowLedger
  if (isRecord(savedLedger) && Array.isArray(savedLedger.entries)) {
    const validEntries = savedLedger.entries
      .map(hydrateCashFlowEntry)
      .filter((entry): entry is CashFlowEntry => entry !== null)
    const migrated = migrateLegacyCashUnits(validEntries, state.club.resources.cash)
    const ledgerProcessedRounds = Array.isArray(savedLedger.processedRounds)
      ? [...new Set(savedLedger.processedRounds.filter((value): value is string => typeof value === 'string'))]
      : []
    ledger = {
      version: 52,
      entries: migrated.entries,
      currentBalance: migrated.entries.length > 0
        ? migrated.entries[migrated.entries.length - 1].balanceAfter
        : millionsToThousands(state.club.resources.cash),
      processedRounds: ledgerProcessedRounds,
    }
    if (migrated.migrated) {
      state = { ...state, club: { ...state.club, resources: { ...state.club.resources, cash: migrated.cash } } }
    }
  } else {
    ledger = createInitialCashFlowLedger(state.club.resources.cash)
  }

  return { ...state, sponsorship, cashFlowLedger: ledger }
}

export function recordFacilityBuildCost(state: GameState, facility: FacilityType, level: FacilityLevel, cost: number): GameState {
  const ledger = addCashFlowEntry(state.cashFlowLedger, state.season?.season ?? 1, state.round, 'facility-build', `Facility build: ${facility} level ${level}`, -millionsToThousands(cost))
  return {
    ...state,
    club: { ...state.club, resources: { ...state.club.resources, cash: money(state.club.resources.cash - cost) } },
    cashFlowLedger: ledger,
  }
}

export function recordStaffMarketTransaction(state: GameState, type: 'hired' | 'replaced' | 'fired' | 'poached', coachName: string, cost: number, compensation = 0): GameState {
  const netAmount = money(compensation - cost)
  const ledger = addCashFlowEntry(state.cashFlowLedger, state.season?.season ?? 1, state.round, 'staff-market', `Staff ${type}: ${coachName}`, millionsToThousands(netAmount))
  return {
    ...state,
    club: { ...state.club, resources: { ...state.club.resources, cash: money(state.club.resources.cash + netAmount) } },
    cashFlowLedger: ledger,
  }
}

export function recordContractPayment(state: GameState, playerName: string, amount: number, type: 'signing-bonus' | 'trigger-bonus' | 'restructure'): GameState {
  const ledger = addCashFlowEntry(state.cashFlowLedger, state.season?.season ?? 1, state.round, 'contracts', `Contract ${type}: ${playerName}`, -millionsToThousands(amount))
  return {
    ...state,
    club: { ...state.club, resources: { ...state.club.resources, cash: money(state.club.resources.cash - amount) } },
    cashFlowLedger: ledger,
  }
}